import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import useMasterMaps, { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, ChevronRight, Save, Send } from "lucide-react";
import { MRF_TYPES, MRF_PRIORITIES } from "../components/MrfBits";

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-200 rounded-sm p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className="font-serif text-2xl tabular-nums mt-0.5" style={{ color: accent || "#0E2240" }}>{value}</div>
    </div>
  );
}

export default function MrfNew() {
  const navigate = useNavigate();
  const maps = useMasterMaps();
  const [form, setForm] = useState({
    branch_id: "", dept_id: "", desig_id: "", requisition_type: "Additional Within Sanction",
    positions_count: 1, priority: "Normal", expected_doj: "", justification: "", replacing_employee_id: "",
  });
  const [check, setCheck] = useState(null);
  const [overrideOk, setOverrideOk] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const desigOptions = useMemo(
    () => (form.dept_id ? maps.designations.filter((d) => d.dept_id === form.dept_id) : maps.designations),
    [form.dept_id, maps.designations]);

  useEffect(() => {
    if (!form.branch_id || !form.dept_id || !form.desig_id) { setCheck(null); return; }
    const t = setTimeout(() => {
      api.post("/mrf/precheck", {
        branch_id: form.branch_id, dept_id: form.dept_id, desig_id: form.desig_id,
        positions_count: Number(form.positions_count) || 1, requisition_type: form.requisition_type,
      }).then(({ data }) => setCheck(data)).catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.branch_id, form.dept_id, form.desig_id, form.positions_count, form.requisition_type]);

  useEffect(() => {
    if (form.requisition_type !== "Replacement" || !form.branch_id) { setEmps([]); return; }
    api.get("/employees", { params: { branch_id: form.branch_id, dept_id: form.dept_id || undefined, limit: 200 } })
      .then(({ data }) => setEmps(data.rows.filter((e) => ["Active", "On Notice"].includes(e.employment_status))))
      .catch(() => setEmps([]));
  }, [form.requisition_type, form.branch_id, form.dept_id]);

  const save = async (action) => {
    if (!form.justification.trim()) { toast.error("Justification is mandatory"); return; }
    if (form.requisition_type === "Replacement" && !form.replacing_employee_id) {
      toast.error("Select the exiting employee being replaced"); return;
    }
    if (check?.has_duplicates && (!overrideOk || !overrideReason.trim())) {
      toast.error("An open requisition already exists — confirm the override and give a reason"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form, action, positions_count: Number(form.positions_count) || 1,
        override_duplicate_confirmed: overrideOk, override_reason: overrideReason,
      };
      const { data } = await api.post("/mrf", payload);
      toast.success(`${data.req_no} ${action === "submit" ? "submitted for approval" : "saved as draft"}`);
      navigate(`/requisitions/${data.req_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="mrf-new-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Raise Requisition</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-6">Position math and the exact approval chain are checked live against the sanction master.</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-sm p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Branch</label>
              <select data-testid="mrf-branch" className={inputCls} value={form.branch_id} onChange={(e) => setForm((p) => ({ ...p, branch_id: e.target.value, replacing_employee_id: "" }))}>
                <option value="">— Select branch —</option>
                {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Department</label>
              <select data-testid="mrf-dept" className={inputCls} value={form.dept_id} onChange={(e) => setForm((p) => ({ ...p, dept_id: e.target.value, desig_id: "", replacing_employee_id: "" }))}>
                <option value="">— Select department —</option>
                {maps.departments.map((d) => <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Designation</label>
              <select data-testid="mrf-desig" className={inputCls} value={form.desig_id} onChange={set("desig_id")}>
                <option value="">— Select designation —</option>
                {desigOptions.map((d) => <option key={d.desig_id} value={d.desig_id}>{`${d.desig_name} (${d.grade})`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Requisition type</label>
              <select data-testid="mrf-type" className={inputCls} value={form.requisition_type} onChange={set("requisition_type")}>
                {MRF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Positions</label>
              <input data-testid="mrf-count" type="number" min="1" className={inputCls} value={form.positions_count} onChange={set("positions_count")} />
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Priority</label>
              <select data-testid="mrf-priority" className={inputCls} value={form.priority} onChange={set("priority")}>
                {MRF_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Expected joining date</label>
              <input data-testid="mrf-doj" type="date" className={inputCls} value={form.expected_doj} onChange={set("expected_doj")} />
            </div>
            {form.requisition_type === "Replacement" && (
              <div>
                <label className="text-xs text-[#6B7280] block mb-1">Replacing (exiting employee)</label>
                <select data-testid="mrf-replacing" className={inputCls} value={form.replacing_employee_id} onChange={set("replacing_employee_id")}>
                  <option value="">— Select employee —</option>
                  {emps.map((e) => (
                    <option key={e.emp_id} value={e.emp_id}>{`${e.full_name} (${e.employee_code}) — ${e.employment_status}`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-[#6B7280] block mb-1">Business justification (mandatory)</label>
            <textarea data-testid="mrf-justification" className={`${inputCls} min-h-[90px]`}
              placeholder="Why is this position needed now?" value={form.justification} onChange={set("justification")} />
          </div>

          {check?.has_duplicates && (
            <div data-testid="mrf-duplicate-warning" className="border border-[#BB162B]/40 bg-[#BB162B]/5 rounded-sm p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-[#BB162B]">
                <ShieldAlert size={16} /> Open requisition already exists for this position
              </div>
              <ul className="mt-2 text-xs text-[#6B7280] space-y-1">
                {check.duplicates.map((d) => <li key={d.req_id} className="tabular-nums">{d.req_no} — {d.status}</li>)}
              </ul>
              <label className="mt-3 flex items-center gap-2 text-xs text-[#0E2240]">
                <input data-testid="mrf-override-check" type="checkbox" checked={overrideOk} onChange={(e) => setOverrideOk(e.target.checked)} />
                I confirm this is not a duplicate and want to proceed
              </label>
              {overrideOk && (
                <input data-testid="mrf-override-reason" className={`${inputCls} mt-2`} placeholder="Override reason (mandatory)"
                  value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button data-testid="mrf-save-draft" disabled={busy} onClick={() => save("draft")}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50">
              <Save size={15} /> Save as Draft
            </button>
            <button data-testid="mrf-submit" disabled={busy} onClick={() => save("submit")}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50">
              <Send size={15} /> Submit for Approval
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!check ? (
            <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-[#6B7280]">
              Select branch, department and designation to see the live sanction check and approval chain.
            </div>
          ) : (
            <>
              <div data-testid="mrf-precheck-panel" className="grid grid-cols-2 gap-3">
                <Stat label="Sanctioned" value={check.sanctioned} />
                <Stat label="On rolls (incl. notice)" value={check.active} />
                <Stat label="Open positions" value={check.open_positions} accent="#1E5AA8" />
                <Stat label="Gap" value={check.gap} accent={check.gap > 0 ? "#BB162B" : "#1B7F3B"} />
              </div>
              {check.would_force_beyond && (
                <div data-testid="mrf-beyond-warning" className="border border-[#D97706]/50 bg-[#D97706]/5 rounded-sm p-4 flex items-start gap-2">
                  <AlertTriangle size={16} className="text-[#D97706] mt-0.5 shrink-0" />
                  <div className="text-xs text-[#0E2240] leading-snug">
                    <span className="font-semibold">Beyond Sanction — auto-applied.</span> On rolls + open requisitions + this request exceeds the sanctioned count.
                    AMD approval is forced into the chain below. Sanctioned strength itself is never edited here.
                  </div>
                </div>
              )}
              <div className="bg-white border border-gray-200 rounded-sm p-5">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Approval chain — {check.rule_name || "no rule matched"}</div>
                <div className="mt-1 text-xs text-[#6B7280]">Treated as <span className="font-semibold text-[#0E2240]">{check.effective_type}</span> · Grade {check.grade}</div>
                <div className="mt-4 space-y-0" data-testid="mrf-chain-preview">
                  {check.chain.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 relative">
                      {i < check.chain.length - 1 && <div className="absolute left-[11px] top-6 bottom-0 w-px bg-gray-200" />}
                      <div className="w-6 h-6 rounded-full bg-[#0E2240] text-white text-[10px] flex items-center justify-center shrink-0 tabular-nums">{c.level}</div>
                      <div className="pb-4">
                        <div className="text-sm text-[#0E2240] font-medium">{c.role.replace(/_/g, " ")}</div>
                        <div className="text-xs text-[#6B7280]">
                          {c.name}{c.delegate_of && <span className="text-[#BF9B30]"> (delegate for {c.delegate_of})</span>} · SLA {c.sla_hours}h
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-[#6B7280] flex items-center gap-1 border-t border-gray-100 pt-3">
                  <ChevronRight size={12} /> If a level breaches its SLA, it auto-escalates to the next level and is recorded.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
